import * as Yup from 'yup';
import { parseISO, isAfter, isValid } from 'date-fns';

import * as _ from 'lodash';

import { regexValidation } from '../../../../../constants/regexValidation';
import {
  calculateTotalIncludingTax,
  calculateTotalPayableAmount,
} from '../../../../../helpers/invoiceHelpers';

import moment from 'moment';
import { SIMPLIFIED_INVOICE_TYPE_TRANSACTION } from '../../../../../config/invoice/constants';

export const FIELDS_MAX_LENGTH = {
  name: 60,
  streetName: 60,
  address: 127,
  VAT: 15,
  buildingNumber: 10,
  postalCode: 5,
  idNumber: 50,
  purchaseOrderNumber: 50,
  contactNumber: 127,
  contractNumber: 127,
  description: 1000,
  paymentTerms: 1000,
  bankName: 1000,
  bankNameEn: 1000,
  accountNumber: 34,
  accountName: 1000,
  specialTaxTreatment: 127,
  notes: 1000,
  contractReference: 50,
  exchangeRate: 15,
  invoicingPeriodStartDate: 10,
  invoicingPeriodEndDate: 10,
  descriptionEn: 1000,
  paymentTermsEn: 1000,
  buildingName: 40,
  block: 10,
  city: 40,
  thirdPartyEnglishName: 60,
  thirdPartyTinNumber: 10,
  thirdPartyCity: 40,
  prepaymentInvoiceNumber: 20,
};

export const discountMaxValidation = ({
  t,
  unitPrice,
  quantity,
  discountAmountSchema,
}) => {
  if (!unitPrice || !quantity) {
    return discountAmountSchema;
  }

  return discountAmountSchema.max(
    +unitPrice * +quantity,
    t('create_invoice_validation.discount_amount'),
  );
};
export const discountPercentageMaxValidation = ({
  t,
  unitPrice,
  quantity,
  discountPercentageSchema,
}) => {
  if (!unitPrice || !quantity) {
    return discountPercentageSchema;
  }

  return discountPercentageSchema.max(
    100,
    t('create_invoice_validation.discount_percentage'),
  );
};

export const invoiceTypeValidationSchema = ({ t, type }) => {
  const defaultShape = {
    invoiceTypeDescription: Yup.string().required(
      t('create_invoice_validation.required'),
    ),
    invoiceTypeTransaction: Yup.string().required(
      t('create_invoice_validation.required'),
    ),
  };

  if (type === 'credit' || type === 'debit') {
    return Yup.object().shape({
      ...defaultShape,
      noteType: Yup.string().required(t('create_invoice_validation.required')),
    });
  } else {
    return Yup.object().shape({
      ...defaultShape,
    });
  }
};

export const invoiceInformationValidationSchema = ({ t }) => {
  return Yup.object().shape({
    currency: Yup.string().required(t('create_invoice_validation.required')),
    exchangeRate: Yup.string().when('currency', {
      is: (currency) => currency && currency !== 'BHD',
      then: Yup.string()
        .required(t('create_invoice_validation.exchange_rate_required'))
        .max(
          FIELDS_MAX_LENGTH.exchangeRate,
          t('create_invoice_validation.max_length'),
        )
        .matches(
          /^[0-9]+(\.[0-9]+)?$/,
          t('create_invoice_validation.exchange_rate_format'),
        )
        .test(
          'is-positive',
          t('create_invoice_validation.exchange_rate_positive'),
          (value) => {
            if (!value) {
              return true;
            }
            return parseFloat(value) > 0;
          },
        ),
      otherwise: Yup.string().notRequired(),
    }),
  });
};

export const sellerIdentificationValidationSchema = ({ t }) => {
  return Yup.object().shape({
    vatNumber: Yup.string()
      .required('')
      .matches(
        /^(20|21|22)[0-9]{13}$/,
        t('create_invoice_validation.vat_format'),
      )
      .length(15, t('create_invoice_validation.vat_length')),
    sellerVatNumber: Yup.string().test(
      'seller-vat-conditional',
      '',
      function (value) {
        const parent = this.parent;
        const isRCMImport = parent?.throughRCM === 'Import';
        const hasAdditionalId =
          parent?.additionalSellerId && parent?.additionalSellerIdType;

        // BR-BH-32: Seller VAT not required for RCM Import of services
        if (isRCMImport) {
          return true;
        }

        // If additional ID is provided, VAT is not required
        if (hasAdditionalId) {
          // If VAT is provided, validate its format
          if (value) {
            if (!/^(20|21|22)[0-9]{13}$/.test(value)) {
              return this.createError({
                message: t('create_invoice_validation.seller_vat_format'),
              });
            }
            if (value.length !== 15) {
              return this.createError({
                message: t('create_invoice_validation.vat_length'),
              });
            }
          }
          return true;
        }

        // BR-BH-33: VAT must be 15 digits starting with 20, 21, or 22
        if (!value) {
          return this.createError({
            // message: t('create_invoice_validation.seller_vat_or_id_required'),
          });
        }

        if (!/^(20|21|22)[0-9]{13}$/.test(value)) {
          return this.createError({
            message: t('create_invoice_validation.seller_vat_format'),
          });
        }

        if (value.length !== 15) {
          return this.createError({
            message: t('create_invoice_validation.vat_length'),
          });
        }

        return true;
      },
    ),
    sellerEnglishName: Yup.string()
      .required('')
      .max(FIELDS_MAX_LENGTH.name, t('create_invoice_validation.max_length'))
      .matches(
        regexValidation.noSpecialCharacters,
        t('create_invoice_validation.no_special_characters'),
      )
      .trim(),
    additionalSellerId: Yup.string()
      .test(
        'seller-id-required-when-type-set',
        t('create_invoice_validation.seller_id_number_required'),
        function (value) {
          const parent = this.parent;
          if (
            parent?.additionalSellerIdType &&
            (!value || value.trim() === '')
          ) {
            return this.createError({
              message: t('create_invoice_validation.seller_id_number_required'),
            });
          }

          return true;
        },
      )
      .test(
        'seller-id-conditional',
        t('create_invoice_validation.seller_identifier_required'),
        function (value) {
          const parent = this.parent;
          const hasVat = !!parent?.sellerVatNumber;

          // If VAT is provided, additional ID is not required
          if (hasVat) {
            // If additional ID is provided, validate it
            if (value) {
              if (!regexValidation.noSpecialCharacters.test(value)) {
                return this.createError({
                  message: t('create_invoice_validation.no_special_characters'),
                });
              }
              if (value.length > FIELDS_MAX_LENGTH.idNumber) {
                return this.createError({
                  message: t('create_invoice_validation.max_length', {
                    max: FIELDS_MAX_LENGTH.idNumber,
                  }),
                });
              }
            }
            return true;
          }

          // If no VAT, additional ID is required
          if (!value) {
            return this.createError({
              message: t('create_invoice_validation.seller_id_number_required'),
            });
          }

          if (!regexValidation.noSpecialCharacters.test(value.trim())) {
            return this.createError({
              message: t('create_invoice_validation.no_special_characters'),
            });
          }

          if (value.length > FIELDS_MAX_LENGTH.idNumber) {
            return this.createError({
              message: t('create_invoice_validation.max_length', {
                max: FIELDS_MAX_LENGTH.idNumber,
              }),
            });
          }

          return true;
        },
      )
      .trim(),
    additionalSellerIdType: Yup.string().test(
      'seller-id-type-conditional',
      t('create_invoice_validation.seller_id_type_required'),
      function (value) {
        const parent = this.parent;
        const hasVat = !!parent?.sellerVatNumber;

        // If VAT is provided, additional ID type is not required
        if (hasVat) {
          return true;
        }

        // If no VAT, additional ID type is required
        if (!value) {
          return this.createError({
            message: t('create_invoice_validation.seller_id_type_required'),
          });
        }

        return true;
      },
    ),
  });
};

export const sellerAddressValidationSchema = ({ t }) => {
  return Yup.object().shape({
    sellerStreetEn: Yup.string()
      .required(t('create_invoice_validation.seller_street_required'))
      .trim()
      .matches(
        regexValidation.noSpecialCharacters,
        t('create_invoice_validation.no_special_characters'),
      )
      .max(
        FIELDS_MAX_LENGTH.streetName,
        t('create_invoice_validation.max_length', {
          max: FIELDS_MAX_LENGTH.streetName,
        }),
      ),
    sellerBuildingName: Yup.string()
      .required(t('create_invoice_validation.seller_building_required'))
      .trim()
      .matches(
        regexValidation.noSpecialCharacters,
        t('create_invoice_validation.no_special_characters'),
      )
      .max(
        FIELDS_MAX_LENGTH.buildingName,
        t('create_invoice_validation.max_length', {
          max: FIELDS_MAX_LENGTH.buildingName,
        }),
      ),
    sellerCity: Yup.string()
      .required(t('create_invoice_validation.seller_city_required'))
      .matches(
        regexValidation.noSpecialCharacters,
        t('create_invoice_validation.no_special_characters'),
      )
      .max(
        FIELDS_MAX_LENGTH.city,
        t('create_invoice_validation.max_length', {
          max: FIELDS_MAX_LENGTH.city,
        }),
      ),
    sellerBlock: Yup.string()
      .required(t('create_invoice_validation.seller_block_required'))
      .trim()
      .matches(
        regexValidation.noSpecialCharacters,
        t('create_invoice_validation.no_special_characters'),
      )
      .max(
        FIELDS_MAX_LENGTH.address,
        t('create_invoice_validation.max_length', {
          max: FIELDS_MAX_LENGTH.address,
        }),
      ),
    sellerFlatNumber: Yup.string()
      .required(t('create_invoice_validation.seller_flat_number_required'))
      .trim()
      .matches(
        regexValidation.noSpecialCharacters,
        t('create_invoice_validation.no_special_characters'),
      )
      .max(
        10,
        t('create_invoice_validation.max_length', {
          max: 10,
        }),
      ),
    sellerCountryCode: Yup.string().required(
      t('create_invoice_validation.seller_country_required'),
    ),
  });
};

export const buyerIdentificationValidationSchema = ({ t }) => {
  return Yup.object().shape({
    // BT-044: Buyer Name
    buyerEnglishName: Yup.string()
      .trim()
      .max(
        FIELDS_MAX_LENGTH.name,
        t('create_invoice_validation.max_length', {
          max: FIELDS_MAX_LENGTH.name,
        }),
      )
      .matches(
        regexValidation.noSpecialCharacters,
        t('create_invoice_validation.no_special_characters'),
      )
      .test(
        'buyer-name-required',
        t('create_invoice_validation.buyer_name_required'),
        function (value) {
          const parent = this.parent;
          const isThirdParty = parent?.specialBilling === 'Third';
          // When Third Party billing, buyer fields are optional
          if (isThirdParty) {
            return true;
          }

          const isSimplified =
            parent?.invoiceTypeTransaction ===
            SIMPLIFIED_INVOICE_TYPE_TRANSACTION;
          const isSummary = parent?.summaryEinvoice === true;

          // BR-BH-43: Buyer name required for:
          // 1. Full Documents (standard invoices)
          // 2. Simplified + Summary transactions
          const requiresBuyerName =
            !isSimplified || (isSimplified && isSummary);

          if (requiresBuyerName) {
            if (!value || value.trim() === '') {
              return false;
            }
          }

          return true;
        },
      ),
    // BT-046-1: Scheme identifier for buyer identifier
    additionalBuyerIdType: Yup.string().test(
      'scheme-id-required-when-buyer-id-provided',
      t('create_invoice_validation.buyer_id_type_required'),
      function (value) {
        const parent = this.parent;
        const isThirdParty = parent?.specialBilling === 'Third';
        // When Third Party billing, buyer fields are optional
        if (isThirdParty) {
          return true;
        }

        const buyerVatNumber = parent?.buyerVatNumber;
        const isExport = parent?.throughRCM === 'Export';
        // If buyer ID is provided, scheme identifier must be provided
        if (
          ((parent?.additionalBuyerId && parent.additionalBuyerId.trim()) ||
            !buyerVatNumber) &&
          !isExport
        ) {
          return !!value;
        }
        return true;
      },
    ),
    // BT-046: Buyer identifier
    additionalBuyerId: Yup.string()
      .max(
        FIELDS_MAX_LENGTH.idNumber,
        t('create_invoice_validation.max_length', {
          max: FIELDS_MAX_LENGTH.idNumber,
        }),
      )
      .matches(
        regexValidation.noSpecialCharacters,
        t('create_invoice_validation.no_special_characters'),
      )
      .trim()
      .test(
        'buyer-id-required-when-type-set',
        t('create_invoice_validation.buyer_id_number_required'),
        function (value) {
          const parent = this.parent;
          const isThirdParty = parent?.specialBilling === 'Third';
          // When Third Party billing, buyer fields are optional
          if (isThirdParty) {
            return true;
          }

          const { additionalBuyerIdType, buyerVatNumber, throughRCM } = parent;
          const isExport = throughRCM === 'Export';

          if (additionalBuyerIdType || (!buyerVatNumber && !isExport)) {
            if (!value || value.trim() === '') {
              return this.createError({
                message: t(
                  'create_invoice_validation.buyer_id_number_required',
                ),
              });
            }
          } else if (value && !regexValidation.trim.test(value)) {
            return this.createError({
              message: t('create_invoice_validation.no_white_space'),
            });
          }
          return true;
        },
      ),
    // BT-048: Buyer VAT Identifier (BR-BH-33)
    buyerVatNumber: Yup.string()
      .matches(
        /^(20|21|22)[0-9]{13}$/,
        t('create_invoice_validation.vat_format'),
      )
      .length(15, t('create_invoice_validation.vat_length'))
      .test(
        'self-billing-vat-required',
        t('create_invoice_validation.buyer_id_required'),
        function (value) {
          const parent = this.parent;
          const isThirdParty = parent?.specialBilling === 'Third';
          // When Third Party billing, buyer fields are optional
          if (isThirdParty) {
            return true;
          }

          const isSelfBilling = parent?.specialBilling === 'Self';

          // BR-BH-44: Self-billing requires buyer VAT number
          if (isSelfBilling) {
            if (!value || value.trim() === '') {
              return false;
            }
          }

          return true;
        },
      ),
  });
};

export const buyerAddressValidationSchema = ({ t }) => {
  return Yup.object().shape({
    // BT-050: Buyer Street Name
    buyerStreetEn: Yup.string()
      .matches(
        regexValidation.trim,
        t('create_invoice_validation.no_white_space'),
      )
      .matches(
        regexValidation.noSpecialCharacters,
        t('create_invoice_validation.no_special_characters'),
      )
      .max(
        FIELDS_MAX_LENGTH.streetName,
        t('create_invoice_validation.max_length', {
          max: FIELDS_MAX_LENGTH.streetName,
        }),
      )
      .test(
        'buyer-street-required',
        t('create_invoice_validation.buyer_street_required'),
        function (value) {
          const parent = this.parent;
          if (!parent) {
            return true;
          }

          const isThirdParty = parent.specialBilling === 'Third';
          // When Third Party billing, buyer fields are optional
          if (isThirdParty) {
            return true;
          }

          const isExport = parent.throughRCM === 'Export';
          const isSimplified =
            parent.invoiceTypeTransaction ===
            SIMPLIFIED_INVOICE_TYPE_TRANSACTION;
          const isSummary = parent.summaryEinvoice === true;

          // BR-BH-46 & BR-BH-47: Full address required for non-export full/summary docs
          const requiresFullAddress =
            (!isSimplified || (isSimplified && isSummary)) && !isExport;

          if (requiresFullAddress && (!value || value.trim() === '')) {
            return false;
          }
          return true;
        },
      ),
    // BT-051: Buyer Block
    buyerBlock: Yup.string()
      .matches(
        regexValidation.trim,
        t('create_invoice_validation.no_white_space'),
      )
      .matches(
        regexValidation.noSpecialCharacters,
        t('create_invoice_validation.no_special_characters'),
      )
      .max(
        FIELDS_MAX_LENGTH.address,
        t('create_invoice_validation.max_length', {
          max: FIELDS_MAX_LENGTH.address,
        }),
      )
      .test(
        'buyer-block-required',
        t('create_invoice_validation.buyer_block_required'),
        function (value) {
          const parent = this.parent;
          if (!parent) {
            return true;
          }

          const isThirdParty = parent.specialBilling === 'Third';
          // When Third Party billing, buyer fields are optional
          if (isThirdParty) {
            return true;
          }

          const isExport = parent.throughRCM === 'Export';
          const isSimplified =
            parent.invoiceTypeTransaction ===
            SIMPLIFIED_INVOICE_TYPE_TRANSACTION;
          const isSummary = parent.summaryEinvoice === true;

          // BR-BH-46 & BR-BH-47: Full address required for non-export full/summary docs
          const requiresFullAddress =
            (!isSimplified || (isSimplified && isSummary)) && !isExport;

          if (requiresFullAddress && (!value || value.trim() === '')) {
            return false;
          }
          return true;
        },
      ),
    // BT-163: Buyer Building Name
    buyerBuildingName: Yup.string()
      .matches(
        regexValidation.trim,
        t('create_invoice_validation.no_white_space'),
      )
      .matches(
        regexValidation.noSpecialCharacters,
        t('create_invoice_validation.no_special_characters'),
      )
      .max(
        FIELDS_MAX_LENGTH.buildingName,
        t('create_invoice_validation.max_length', {
          max: FIELDS_MAX_LENGTH.buildingName,
        }),
      )
      .test(
        'buyer-building-required',
        t('create_invoice_validation.buyer_building_required'),
        function (value) {
          const parent = this.parent;
          if (!parent) {
            return true;
          }

          const isThirdParty = parent.specialBilling === 'Third';
          // When Third Party billing, buyer fields are optional
          if (isThirdParty) {
            return true;
          }

          const isExport = parent.throughRCM === 'Export';
          const isSimplified =
            parent.invoiceTypeTransaction ===
            SIMPLIFIED_INVOICE_TYPE_TRANSACTION;
          const isSummary = parent.summaryEinvoice === true;

          // BR-BH-46 & BR-BH-47: Full address required for non-export full/summary docs
          const requiresFullAddress =
            (!isSimplified || (isSimplified && isSummary)) && !isExport;

          if (requiresFullAddress && (!value || value.trim() === '')) {
            return false;
          }
          return true;
        },
      ),
    // BT-BH-013: Buyer Flat/Shop Number
    buyerFlatNumber: Yup.string()
      .matches(
        regexValidation.noSpecialCharacters,
        t('create_invoice_validation.no_special_characters'),
      )
      .max(
        10,
        t('create_invoice_validation.max_length', {
          max: 10,
        }),
      )
      .test(
        'buyer-unit-required',
        t('create_invoice_validation.buyer_unit_required'),
        function (value) {
          const parent = this.parent;
          if (!parent) {
            return true;
          }

          const isThirdParty = parent.specialBilling === 'Third';
          // When Third Party billing, buyer fields are optional
          if (isThirdParty) {
            return true;
          }

          const isExport = parent.throughRCM === 'Export';
          const isSimplified =
            parent.invoiceTypeTransaction ===
            SIMPLIFIED_INVOICE_TYPE_TRANSACTION;
          const isSummary = parent.summaryEinvoice === true;

          // BR-BH-46 & BR-BH-47: Full address required for non-export full/summary docs
          const requiresFullAddress =
            (!isSimplified || (isSimplified && isSummary)) && !isExport;

          if (requiresFullAddress && (!value || value.trim() === '')) {
            return false;
          }
          return true;
        },
      ),
    // BT-052: Buyer City
    buyerCity: Yup.string()
      .matches(
        regexValidation.noSpecialCharacters,
        t('create_invoice_validation.no_special_characters'),
      )
      .max(
        FIELDS_MAX_LENGTH.city,
        t('create_invoice_validation.max_length', {
          max: FIELDS_MAX_LENGTH.city,
        }),
      )
      .test(
        'buyer-city-required',
        t('create_invoice_validation.buyer_city_required'),
        function (value) {
          const parent = this.parent;
          if (!parent) {
            return true;
          }

          const isThirdParty = parent.specialBilling === 'Third';
          // When Third Party billing, buyer fields are optional
          if (isThirdParty) {
            return true;
          }

          const isExport = parent.throughRCM === 'Export';
          const isSimplified =
            parent.invoiceTypeTransaction ===
            SIMPLIFIED_INVOICE_TYPE_TRANSACTION;
          const isSummary = parent.summaryEinvoice === true;

          // BR-BH-46 & BR-BH-47: Full address required for non-export full/summary docs
          const requiresFullAddress =
            (!isSimplified || (isSimplified && isSummary)) && !isExport;

          if (requiresFullAddress && (!value || value.trim() === '')) {
            return false;
          }
          return true;
        },
      ),
    // BT-055: Buyer Country Code (Always required per BR-BH-47, BR-BH-48)
    buyerCountryCode: Yup.string().test(
      'buyer-country-required',
      t('create_invoice_validation.buyer_country_required'),
      function (value) {
        const parent = this.parent;
        if (!parent) {
          return true;
        }

        const isThirdParty = parent.specialBilling === 'Third';
        // When Third Party billing, buyer fields are optional
        if (isThirdParty) {
          return true;
        }

        const isExport = parent.throughRCM === 'Export';
        const isSimplified =
          parent.invoiceTypeTransaction === SIMPLIFIED_INVOICE_TYPE_TRANSACTION;
        const isSummary = parent.summaryEinvoice === true;

        // BR-BH-46 & BR-BH-47: Full address required for non-export full/summary docs
        const requiresFullAddress =
          !isSimplified || (isSimplified && isSummary) || isExport;

        if (requiresFullAddress && (!value || value.trim() === '')) {
          return false;
        }
        return true;
      },
    ),
  });
};

export const orderReferenceValidationSchema = ({ t }) => {
  return Yup.object().shape({
    purchaseOrderNumber: Yup.string()
      .max(
        FIELDS_MAX_LENGTH.purchaseOrderNumber,
        t('create_invoice_validation.max_length'),
      )
      .matches(
        regexValidation.trim,
        t('create_invoice_validation.no_white_space'),
      )
      .matches(
        regexValidation.noSpecialCharacters,
        t('create_invoice_validation.no_special_characters'),
      ),
  });
};

export const lineItemsValidationSchema = ({ t }) => {
  const tempItemShape = () => ({
    descriptionEn: Yup.string()
      .required(t('create_invoice_validation.item_desc_required'))
      .max(
        FIELDS_MAX_LENGTH.descriptionEn,
        t('create_invoice_validation.max_length'),
      )
      .matches(
        regexValidation.trim,
        t('create_invoice_validation.no_white_space'),
      )
      .matches(
        regexValidation.noSpecialCharacters,
        t('create_invoice_validation.no_special_characters'),
      ),
    unitPrice: Yup.string()
      .required(t('create_invoice_validation.unitPrice_required'))
      .max(20, t('create_invoice_validation.max_length'))
      .matches(regexValidation.numberWithMaxThreeDecimals, {
        message: t('create_invoice_validation.unitPrice'),
        excludeEmptyString: true,
      })
      .typeError(t('create_invoice_validation.unitPrice'))
      .test(
        'unitPrice-positive',
        t('create_invoice_validation.more_than_zero'),
        (value) =>
          !value || (value && +value > 0 && String(value).indexOf('-') === -1),
      ),
    quantity: Yup.string()
      .required(t('create_invoice_validation.quantityRequired'))
      .matches(
        regexValidation.numberWithMaxSixDecimals,
        t('create_invoice_validation.quantity'),
      )
      .typeError(t('create_invoice_validation.quantity'))
      .test(
        'quantity-positive',
        t('create_invoice_validation.quantity'),
        (value) => Number(value) > 0,
      ),
    discountAmount: Yup.string()
      .matches(
        regexValidation.numberWithMaxSixDecimals,
        t('create_invoice_validation.discount_amount'),
      )
      .typeError(t('create_invoice_validation.discount_amount'))
      .test(
        'discountAmount-non-negative',
        t('create_invoice_validation.discount_amount'),
        (value) =>
          !value || (value && +value >= 0 && String(value).indexOf('-') === -1),
      )
      .when(
        ['unitPrice', 'quantity'],
        (unitPrice, quantity, discountAmountSchema) => {
          if (!unitPrice || !quantity) {
            return discountAmountSchema;
          }
          return discountAmountSchema.test(
            'discountAmount-max',
            t('create_invoice_validation.discount_amount'),
            (value) => !value || +value <= +unitPrice * +quantity,
          );
        },
      ),
    discountPercentage: Yup.string()
      .matches(
        regexValidation.numberWithMaxSixDecimals,
        t('create_invoice_validation.discount_percentage'),
      )
      .typeError(t('create_invoice_validation.discount_percentage'))
      .test(
        'discountPercentage-range',
        t('create_invoice_validation.discount_percentage'),
        (value) =>
          !value ||
          (value &&
            +value >= 0 &&
            +value < 100 &&
            String(value).indexOf('-') === -1),
      ),
    vatRateType: Yup.string().required(
      t('create_invoice_validation.vatRateTypeRequired'),
    ),
  });

  return Yup.object().shape({
    tempItem: Yup.object().when('isCreatingItem', (v) =>
      v ? Yup.object().shape({ ...tempItemShape() }) : Yup.object(),
    ),
  });
};

export const paymentTermsValidationSchema = ({ t }) => {
  return Yup.object().shape({
    paymentMethod: Yup.string(),
    paymentDate: Yup.string().test(
      'payment-date-not-future',
      t('create_invoice_validation.payment_date_future'),
      function (value) {
        // BR-BH-18: Payment date must not be in the future
        if (!value) {
          return true;
        }
        const date = moment(value, 'DD/MM/YYYYY');
        return date.isValid() && !moment().isBefore(date);
      },
    ),
    paidAmount: Yup.mixed().test(
      'paid-amount-max',
      t('create_invoice_validation.paid_amount_exceeds_total'),
      function (value) {
        // BR-BH-67: Paid amount cannot exceed invoice total including VAT
        // paidAmount is a boolean switch; skip validation when it is falsy
        if (!value || value === false || value === 'false') {
          return true;
        }
        // Get invoice total from root form context
        const root = this.from[this.from.length - 1].value;

        const totalAmount = Number(
          calculateTotalIncludingTax(root.items || [], root),
        );

        return value <= totalAmount;
      },
    ),
    bankNameEn: Yup.string()
      .max(
        FIELDS_MAX_LENGTH.bankNameEn,
        t('create_invoice_validation.max_length'),
      )
      .when(['paymentMethod'], (paymentMethod, bankNameSchema) => {
        const methodsNeedingBankDetails = [
          'CREDIT_TRANSFER',
          'DEBIT_CARD',
          'CHEQUE',
          'BANKERS_DRAFT',
        ];
        return methodsNeedingBankDetails.includes(paymentMethod)
          ? bankNameSchema.required(t('create_invoice_validation.required'))
          : bankNameSchema;
      })
      .matches(
        regexValidation.trim,
        t('create_invoice_validation.no_white_space'),
      )
      .matches(
        regexValidation.noSpecialCharacters,
        t('create_invoice_validation.no_special_characters'),
      ),
    accountNumber: Yup.string()
      .max(
        FIELDS_MAX_LENGTH.accountNumber,
        t('create_invoice_validation.max_length'),
      )
      .when(['paymentMethod'], (paymentMethod, accountNumberSchema) => {
        // Show for methods that need account details
        const methodsNeedingAccount = [
          'CREDIT_TRANSFER',
          'DEBIT_CARD',
          'CHEQUE',
          'BANKERS_DRAFT',
        ];
        return methodsNeedingAccount.includes(paymentMethod)
          ? accountNumberSchema.required(
              t('create_invoice_validation.required'),
            )
          : accountNumberSchema;
      })
      .matches(
        regexValidation.trim,
        t('create_invoice_validation.no_white_space'),
      ),
    accountName: Yup.string()
      .max(
        FIELDS_MAX_LENGTH.accountName,
        t('create_invoice_validation.max_length'),
      )
      .when(['paymentMethod'], (paymentMethod, accountNameSchema) => {
        // Show for methods that need account details
        const methodsNeedingAccount = [
          'CREDIT_TRANSFER',
          'DEBIT_CARD',
          'CHEQUE',
          'BANKERS_DRAFT',
        ];
        return methodsNeedingAccount.includes(paymentMethod)
          ? accountNameSchema.required(t('create_invoice_validation.required'))
          : accountNameSchema;
      })
      .matches(
        regexValidation.trim,
        t('create_invoice_validation.no_white_space'),
      ),
    paymentTermsEn: Yup.string()
      .max(
        FIELDS_MAX_LENGTH.paymentTermsEn,
        t('create_invoice_validation.max_length'),
      )
      .matches(
        regexValidation.trim,
        t('create_invoice_validation.no_white_space'),
      )
      .matches(
        regexValidation.noSpecialCharacters,
        t('create_invoice_validation.no_special_characters'),
      ),
  });
};

export const thirdPartyValidationSchema = ({ t }) => {
  return Yup.object().shape({
    // BR-BH-38, BR-BH-39, BR-BH-40: Third Party VAT or TIN validation
    thirdPartyVatNumber: Yup.string()
      .test(
        'third-party-vat-or-tin',
        t('create_invoice_validation.vat_format'),
        function (value) {
          if (!value) {
            return true;
          } // Handled by conditional required

          const specialBillingCheck = this.parent?.specialBillingCheck;
          const specialBilling = this.parent?.specialBilling;

          // Only validate if third-party billing selected
          if (!(specialBillingCheck && specialBilling === 'Third')) {
            return true;
          }

          // BR-BH-38: Accept Third Party VAT format (15 digits, starts with 2)
          const thirdPartyVatFormat = /^(20|21|22)[0-9]{13}$/;
          // BR-BH-39: Accept TIN format (10 digits, starts with 20 or 22)

          if (thirdPartyVatFormat.test(value)) {
            return true; // Valid third-party VAT
          }

          return this.createError({
            message: t('create_invoice_validation.vat_format'),
          });
        },
      )
      .when(
        ['specialBillingCheck', 'specialBilling'],
        (specialBillingCheck, specialBilling, schema) => {
          if (specialBillingCheck && specialBilling === 'Third') {
            return schema.required(t('create_invoice_validation.required'));
          }
          return schema;
        },
      ),
    // BR-BH-37: Third Party Name
    thirdPartyEnglishName: Yup.string()
      .max(
        FIELDS_MAX_LENGTH.thirdPartyEnglishName,
        t('create_invoice_validation.max_length'),
      )
      .when(
        ['specialBillingCheck', 'specialBilling'],
        (specialBillingCheck, specialBilling, schema) => {
          if (specialBillingCheck && specialBilling === 'Third') {
            return schema.required(t('create_invoice_validation.required'));
          }
          return schema;
        },
      )
      .matches(
        regexValidation.noSpecialCharacters,
        t('create_invoice_validation.no_special_characters'),
      )
      .trim(),

    // BT-BH-034: Third Party Address Line 1 - Street Name (BR-BH-42)
    thirdPartyStreetEn: Yup.string()
      .max(
        FIELDS_MAX_LENGTH.streetName,
        t('create_invoice_validation.max_length', {
          max: FIELDS_MAX_LENGTH.streetName,
        }),
      )
      .when(
        ['specialBillingCheck', 'specialBilling'],
        (specialBillingCheck, specialBilling, schema) => {
          if (specialBillingCheck && specialBilling === 'Third') {
            return schema.required(
              t('create_invoice_validation.third_party_street_required'),
            );
          }
          return schema;
        },
      )
      .matches(
        regexValidation.noSpecialCharacters,
        t('create_invoice_validation.no_special_characters'),
      )
      .trim(),
    // BT-BH-035: Third Party Address Line 2 - Block (BR-BH-42)
    thirdPartyBlock: Yup.string()
      .max(FIELDS_MAX_LENGTH.block, t('create_invoice_validation.max_length'))
      .when(
        ['specialBillingCheck', 'specialBilling'],
        (specialBillingCheck, specialBilling, schema) => {
          if (specialBillingCheck && specialBilling === 'Third') {
            return schema.required(
              t('create_invoice_validation.third_party_block_required'),
            );
          }
          return schema;
        },
      )
      .matches(
        regexValidation.noSpecialCharacters,
        t('create_invoice_validation.no_special_characters'),
      )
      .trim(),
    // BT-BH-036: Third Party Address Line 3 - Building Name (BR-BH-42)
    thirdPartyBuildingName: Yup.string()
      .max(
        FIELDS_MAX_LENGTH.buildingName,
        t('create_invoice_validation.max_length'),
      )
      .when(
        ['specialBillingCheck', 'specialBilling'],
        (specialBillingCheck, specialBilling, schema) => {
          if (specialBillingCheck && specialBilling === 'Third') {
            return schema.required(
              t('create_invoice_validation.third_party_building_required'),
            );
          }
          return schema;
        },
      )
      .matches(
        regexValidation.noSpecialCharacters,
        t('create_invoice_validation.no_special_characters'),
      )
      .trim(),
    // BT-BH-037: Third Party Address Line 4 - Flat/Shop No (BR-BH-42)
    thirdPartyFlatNumber: Yup.string()
      .max(10, t('create_invoice_validation.max_length'))
      .when(
        ['specialBillingCheck', 'specialBilling'],
        (specialBillingCheck, specialBilling, schema) => {
          if (specialBillingCheck && specialBilling === 'Third') {
            return schema.required(
              t('create_invoice_validation.third_party_flat_required'),
            );
          }
          return schema;
        },
      )
      .matches(
        regexValidation.noSpecialCharacters,
        t('create_invoice_validation.no_special_characters'),
      )
      .trim(),
    // BT-BH-038: Third Party City (BR-BH-42)
    thirdPartyCity: Yup.string()
      .max(
        FIELDS_MAX_LENGTH.city,
        t('create_invoice_validation.max_length', {
          max: FIELDS_MAX_LENGTH.city,
        }),
      )
      .when(
        ['specialBillingCheck', 'specialBilling'],
        (specialBillingCheck, specialBilling, schema) => {
          if (specialBillingCheck && specialBilling === 'Third') {
            return schema.required(
              t('create_invoice_validation.third_party_city_required'),
            );
          }
          return schema;
        },
      )
      .matches(
        regexValidation.noSpecialCharacters,
        t('create_invoice_validation.no_special_characters'),
      )
      .trim(),
    // BR-BH-40: Third Party TIN Number
    thirdPartyTinNumber: Yup.string()
      .max(
        FIELDS_MAX_LENGTH.thirdPartyTinNumber,
        t('create_invoice_validation.max_length'),
      )
      .when(
        ['specialBillingCheck', 'specialBilling'],
        (specialBillingCheck, specialBilling, schema) => {
          if (specialBillingCheck && specialBilling === 'Third') {
            return schema.required(t('create_invoice_validation.required'));
          }
          return schema;
        },
      )
      .matches(
        regexValidation.noSpecialCharacters,
        t('create_invoice_validation.no_special_characters'),
      )
      .trim(),
    // BT-BH-041: Third Party Country Code (BR-BH-42)
    thirdPartyCountryCode: Yup.string().when(
      ['specialBillingCheck', 'specialBilling'],
      (specialBillingCheck, specialBilling, schema) => {
        if (specialBillingCheck && specialBilling === 'Third') {
          return schema.required(
            t('create_invoice_validation.third_party_country_required'),
          );
        }
        return schema;
      },
    ),
  });
};

export const ALL_VALIDATIONS = ({ t, invoiceType }) => {
  return Yup.object()
    .shape({
      notesEn: Yup.string()
        .max(FIELDS_MAX_LENGTH.notes, t('create_invoice_validation.max_length'))
        .matches(
          regexValidation.trim,
          t('create_invoice_validation.no_white_space'),
        )
        .matches(
          regexValidation.noSpecialCharacters,
          t('create_invoice_validation.no_special_characters'),
        ),
      precedingInvoiceNumber: Yup.string().when('documentType', {
        is: (val) => val === 'credit' || val === 'debit',
        then: Yup.string()
          .required(
            t('create_invoice_validation.preceding_invoice_number_required'),
          )
          .matches(
            regexValidation.noSpecialCharacters,
            t('create_invoice_validation.no_special_characters'),
          ),
      }),
      precedingInvoiceIssueDate: Yup.string().when('documentType', {
        is: (val) => val === 'credit' || val === 'debit',
        then: Yup.string()
          .required(
            t(
              'create_invoice_validation.preceding_invoice_issue_date_required',
            ),
          )
          .test(
            'is-valid-date',
            t('create_invoice_validation.invalid_date'),
            (value) => {
              if (!value) {
                return true;
              }
              return moment(value, 'DD/MM/YYYY', true).isValid();
            },
          )
          .test(
            'not-future-date',
            t('create_invoice_validation.future_date'),
            (value) => {
              if (!value) {
                return true;
              }
              return !moment(value, 'DD/MM/YYYY').isAfter(moment());
            },
          ),
      }),
      noteIssuingReason: Yup.string().when('documentType', {
        is: (val) => val === 'credit' || val === 'debit',
        then: Yup.string()
          .required(t('create_invoice_validation.note_issuing_reason_required'))
          .oneOf(
            ['RA', 'RB', 'RC', 'RD', 'RE', 'RF', 'RG', 'RH', 'RI', 'OTH'],
            t('create_invoice_validation.invalid_note_reason'),
          ),
      }),
      invoicingPeriodEndDate: Yup.string()
        .when('summaryEinvoice', {
          is: true,
          then: Yup.string().required(
            t('create_invoice_validation.invoicingPeriodEndDateRequired'),
          ),
        })
        .test(
          'is-not-future',
          t('create_invoice_validation.invoicing_period_future'),
          (value) => {
            if (!value) {
              return true;
            }
            const date = moment(value, 'DD/MM/YYYYY');
            return date.isValid() && !moment().isBefore(date);
          },
        ),
      zeroRatingReasonInput: Yup.string().when('items', {
        is: (items) => items?.some((item) => item?.vatRateType === 'Z'),
        then: Yup.string().required(t('create_invoice_validation.required')),
        otherwise: Yup.string().notRequired(),
      }),
      exemptionReasonCode: Yup.string().when('items', {
        is: (items) => items?.some((item) => item?.vatRateType === 'E'),
        then: Yup.string().required(t('create_invoice_validation.required')),
        otherwise: Yup.string().notRequired(),
      }),
      outOfScopeReasonInput: Yup.string().when('items', {
        is: (items) => items?.some((item) => item?.vatRateType === 'O'),
        then: Yup.string().required(t('create_invoice_validation.required')),
        otherwise: Yup.string().notRequired(),
      }),
    })
    .test(
      'no-preceding-data-for-invoice',
      t('create_invoice_validation.invalid_preceding_invoice_data'),
      function (values) {
        // BR-BH-28: If document is regular invoice, preceding invoice fields must not be provided
        if (values.documentType === 'invoice') {
          return (
            !values.precedingInvoiceNumber &&
            !values.precedingInvoiceIssueDate &&
            !values.noteIssuingReason
          );
        }
        return true;
      },
    )
    .test(
      'special-scenarios-check',
      t('create_invoice_validation.special_scenario_conflict'),
      function (values) {
        if (!values.specialScenario) {
          return true;
        }

        let errors = [];

        // Helper flags for readability
        const isSelfBilling =
          values.specialBillingCheck && values.specialBilling === 'Self';
        const isThirdParty =
          values.specialBillingCheck && values.specialBilling === 'Third';
        const isExport =
          values.throughRCMCheck && values.throughRCM === 'Export';
        const isImport =
          values.throughRCMCheck && values.throughRCM === 'Import';
        const isDeemed = !!values.deemedSupply;
        const isContinuous = !!values.continuousSupply;
        const isDomestic = !!values.domesticSupply;
        const isSummary = !!values.summaryEinvoice;
        const isPrepayment = !!values.prepaymentInvoice;

        // BR-BH-03: Summary + Prepayment (and Summary + Continuous)
        if (isSummary && (isPrepayment || isContinuous)) {
          errors.push(
            this.createError({
              path: 'summaryEinvoice',
              message: isContinuous
                ? t('create_invoice_validation.summary_continuous_conflict')
                : t('create_invoice_validation.summary_prepayment_conflict'),
            }),
          );
        }

        // BR-BH-04: Self-billing + Third-party
        if (isSelfBilling && isThirdParty) {
          errors.push(
            this.createError({
              path: 'specialBillingCheck',
              message: t('create_invoice_validation.self_third_conflict'),
            }),
          );
        }

        // BR-BH-06, 08, 10: Self-billing vs (RCM / Deemed / Domestic)
        if (isSelfBilling && (isExport || isImport || isDeemed || isDomestic)) {
          errors.push(
            this.createError({
              path: 'specialBillingCheck',
              message: t(
                'create_invoice_validation.self_billing_transaction_conflict',
              ),
            }),
          );
        }

        // BR-BH-05, BR-BH-DEEMED: Mutual exclusivity of RCM/Deemed/Domestic
        // (Export vs Import, Export vs Domestic, Import vs Domestic, Deemed vs all)
        if (isExport && (isImport || isDomestic || isDeemed)) {
          errors.push(
            this.createError({
              path: 'throughRCMCheck',
              message: t('create_invoice_validation.rcm_conflict'),
            }),
          );
        }
        if (isImport && (isDomestic || isDeemed)) {
          errors.push(
            this.createError({
              path: 'throughRCMCheck',
              message: t('create_invoice_validation.rcm_conflict'),
            }),
          );
        }
        if (isDeemed && (isDomestic || isContinuous)) {
          errors.push(
            this.createError({
              path: 'deemedSupply',
              message: t('create_invoice_validation.deemed_supply_conflict'),
            }),
          );
        }

        // BR-BH-09: Continuous Supply + Prepayment
        if (isContinuous && isPrepayment) {
          errors.push(
            this.createError({
              path: 'continuousSupply',
              message: t(
                'create_invoice_validation.continuous_prepayment_conflict',
              ),
            }),
          );
        }

        if (errors.length > 0) {
          throw new Yup.ValidationError(errors);
        }

        return true;
      },
    )
    .test(
      'invoicing-period-check',
      t('create_invoice_validation.invoicing_period_required'),
      function (values) {
        // BR-BH-19, BR-BH-20, BR-BH-21: Invoicing period required for Summary or Continuous Supply
        if (values.summaryEinvoice || values.continuousSupply) {
          if (
            !values.invoicingPeriodStartDate ||
            !values.invoicingPeriodEndDate
          ) {
            return this.createError({
              path: 'invoicingPeriodStartDate',
              message: t('create_invoice_validation.invoicing_period_required'),
            });
          }

          // BR-BH-21: Start date must be before or equal to end date
          if (
            values.invoicingPeriodStartDate &&
            values.invoicingPeriodEndDate
          ) {
            const startDate = parseISO(values.invoicingPeriodStartDate);
            const endDate = parseISO(values.invoicingPeriodEndDate);
            if (
              isValid(startDate) &&
              isValid(endDate) &&
              isAfter(startDate, endDate)
            ) {
              return this.createError({
                path: 'invoicingPeriodEndDate',
                message: t('create_invoice_validation.end_date_before_start'),
              });
            }
          }
        }
        return true;
      },
    )
    .test(
      'rounding-amount-check',
      t('create_invoice_validation.rounding_amount_exceeded'),
      function (values) {
        // Edge case: Rounding amount should not exceed 1 currency unit
        if (values.roundingAmountSwitch && values.roundingAmount) {
          const roundingAmount = Math.abs(+values.roundingAmount);
          if (roundingAmount > 1) {
            return this.createError({
              path: 'roundingAmount',
              message: t('create_invoice_validation.rounding_amount_exceeded'),
            });
          }
        }
        return true;
      },
    )
    .test(
      'line-items-uniqueness-check',
      t('create_invoice_validation.duplicate_line_items'),
      function (values) {
        // Edge case: Check for duplicate line item descriptions
        const lineItems = values.lineItems || [];
        if (lineItems.length > 1) {
          const descriptions = lineItems
            .map((item) => (item.descriptionEn || '').toLowerCase().trim())
            .filter(Boolean);

          const uniqueDescriptions = new Set(descriptions);
          if (descriptions.length !== uniqueDescriptions.size) {
            return this.createError({
              path: 'lineItems',
              message: t('create_invoice_validation.duplicate_line_items'),
            });
          }
        }
        return true;
      },
    )
    .test(
      'total-amount-max-length',
      t('create_invoice_validation.total_amount_max_length'),
      function (values) {
        // Data dictionary: Total (Amount due for payment) must not exceed 20 characters
        const items = values.items || [];
        if (items.length === 0) {
          return true;
        }
        const total = calculateTotalPayableAmount(items, values);
        const totalStr = String(total);
        return totalStr.length <= 20;
      },
    )
    .test(
      'discount-vat-type-check',
      t('create_invoice_validation.discount_vat_not_matched'),
      function (values) {
        if (
          values.einvoiceLevelDiscount &&
          values.invoiceDiscountLineItems.length > 0
        ) {
          const discountItemsVatRateTypes = values.invoiceDiscountLineItems.map(
            (item) => item.discountVatRateType,
          );

          const itemsVatRateTypes = values.items.map(
            (item) => item.vatRateType,
          );

          if (
            _.difference(
              _.uniq(discountItemsVatRateTypes.sort()),
              _.uniq(itemsVatRateTypes.sort()),
            ).length > 0
          ) {
            return this.createError({
              path: 'discountVatRateType',
              message: t('create_invoice_validation.discount_vat_not_matched'),
            });
          }
        }
        return true;
      },
    )
    .concat(invoiceTypeValidationSchema({ t, type: invoiceType }))
    .concat(invoiceInformationValidationSchema({ t }))
    .concat(sellerIdentificationValidationSchema({ t }))
    .concat(sellerAddressValidationSchema({ t }))
    .concat(buyerIdentificationValidationSchema({ t }))
    .concat(buyerAddressValidationSchema({ t }))
    .concat(orderReferenceValidationSchema({ t }))
    .concat(lineItemsValidationSchema({ t }))
    .concat(paymentTermsValidationSchema({ t }))
    .concat(thirdPartyValidationSchema({ t }));
};

export const discountLineItemSchema = ({ t }) => {
  return Yup.object().shape({
    type: Yup.string().required(t('create_invoice_validation.required')),
    discountAmount: Yup.string()
      .nullable()
      .when('type', {
        is: 'amount',
        then: (schema) =>
          schema
            .required(t('create_invoice_validation.required'))
            .max(20, t('create_invoice_validation.max_length', { max: 20 }))
            .matches(regexValidation.numberWithMaxThreeDecimals, {
              message: t(
                'create_invoice_validation.invoice_discount_amount_format',
              ),
              excludeEmptyString: true,
            }),
        otherwise: (schema) => schema.notRequired(),
      }),
    amountSubjectToDiscount: Yup.string()
      .nullable()
      .when('type', {
        is: 'subject_amount',
        then: (schema) =>
          schema
            .required(
              t('create_invoice_validation.discountable_amount_required'),
            )
            .max(20, t('create_invoice_validation.max_length', { max: 20 }))
            .matches(regexValidation.numberWithMaxThreeDecimals, {
              message: t(
                'create_invoice_validation.discountable_amount_format',
              ),
              excludeEmptyString: true,
            }),
        otherwise: (schema) => schema.notRequired(),
      }),
    rate: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === '' || originalValue === null ? undefined : value,
      )
      .typeError(t('create_invoice_validation.min_max', { min: 1, max: 100 }))
      .min(1, t('create_invoice_validation.min_max', { min: 1, max: 100 }))
      .max(100, t('create_invoice_validation.min_max', { min: 1, max: 100 }))
      .when('type', {
        is: 'subject_amount',
        then: (schema) =>
          schema.required(
            t('create_invoice_validation.discount_rate_required'),
          ),
        otherwise: (schema) => schema.notRequired(),
      }),
    discountVatRateType: Yup.string().required(
      t('discount.validation.invoice_discount_req'),
    ),
  });
};

export const discountValidationSchema = ({ t }) => {
  return Yup.object().shape({
    tempItem: discountLineItemSchema({ t }).when(function (value) {
      return discountLineItemSchema({ t });
    }),
    items: Yup.array().of(discountLineItemSchema({ t })),
  });
};

export const paymentLineItemSchema = ({ t }) =>
  Yup.object().shape({
    invoiceNumber: Yup.string()
      .required(
        t('create_invoice_validation.prepayment_invoice_number_required'),
      )
      .max(
        FIELDS_MAX_LENGTH.prepaymentInvoiceNumber,
        t('create_invoice_validation.prepayment_invoice_number_max_length'),
      )
      .matches(
        regexValidation.noSpecialCharacters,
        t('create_invoice_validation.no_special_characters'),
      ),
    invoiceIssueDate: Yup.string()
      .required(t('create_invoice_validation.prepayment_invoice_date_required'))
      .test(
        'is-valid-date',
        t('create_invoice_validation.invalid_date'),
        (value) => {
          if (!value) {
            return true;
          }
          return moment(value, 'DD/MM/YYYY', true).isValid();
        },
      )
      .test(
        'not-future-date',
        t('create_invoice_validation.prepayment_future_date'),
        (value) => {
          if (!value) {
            return true;
          }
          return !moment(value, 'DD/MM/YYYY').isAfter(moment());
        },
      ),
    taxableAmount: Yup.string()
      .required(
        t('create_invoice_validation.prepayment_taxable_amount_required'),
      )
      .max(20, t('create_invoice_validation.max_length', { max: 20 }))
      .matches(regexValidation.numberWithMaxThreeDecimals, {
        message: t(
          'create_invoice_validation.prepayment_taxable_amount_format',
        ),
        excludeEmptyString: true,
      }),
    vatRate: Yup.string().required(
      t('create_invoice_validation.prepayment_vat_rate_required'),
    ),
  });

export const paymentValidationSchema = ({ t }) =>
  Yup.object().shape({
    tempLineItem: paymentLineItemSchema({ t }).when(function (value) {
      return paymentLineItemSchema({ t });
    }),
    lineItems: Yup.array().of(paymentLineItemSchema({ t })),
  });
